import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout/MainLayout';
import { orderService } from '../../api/services/orderService';
import { returnService, ReturnTag } from '../../api/services/returnService';
import { toast } from 'react-hot-toast';

interface OrderItem {
  Id: number;
  Ordinal: number;
  PartNumber: string;
  Description: string;
  Price: number;
  StatusName: string;
}

type ReturnType = 'Repair' | 'Return';

const CreateReturn: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [returnTags, setReturnTags] = useState<ReturnTag[]>([]);

  // Form state
  const [selectedPartNumbers, setSelectedPartNumbers] = useState<string[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [returnReason, setReturnReason] = useState<string>('');
  const [returnType, setReturnType] = useState<ReturnType>('Repair');
  const [freeShippingLabels, setFreeShippingLabels] = useState<boolean>(false);
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerName, setCustomerName] = useState<string>('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const loadData = async () => {
    if (!orderId) return;

    setLoading(true);
    try {
      const [orderResult, tagsResult] = await Promise.all([
        orderService.getOrderDetail(parseInt(orderId)),
        returnService.getReturnTags()
      ]);
      setOrder(orderResult);
      setReturnTags(tagsResult);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemToggle = (partNumber: string) => {
    setSelectedPartNumbers(prev =>
      prev.includes(partNumber)
        ? prev.filter(pn => pn !== partNumber)
        : [...prev, partNumber]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (selectedPartNumbers.length === 0) {
      toast.error('Please select at least one item to return.');
      return;
    }

    if (!selectedTagId) {
      toast.error('Please select how the order should be tagged.');
      return;
    }

    if (!returnReason.trim()) {
      toast.error('Please enter a return reason.');
      return;
    }

    // Get current user
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const createdBy = user.UserName || 'system';

    setSubmitting(true);
    try {
      const result = await returnService.createReturn({
        orderId: parseInt(orderId || '0'),
        recipientName: customerName,
        recipientEmail: customerEmail,
        description: returnReason,
        returnTagId: selectedTagId,
        returnType: returnType,
        hasReturnPackage: freeShippingLabels,
        partNumbers: selectedPartNumbers,
        createdBy
      });

      const emailMessage = customerEmail ? ' and email sent to customer' : '';
      toast.success(`Return authorization ${result.AuthorizationNumber} created successfully${emailMessage}!`);
      navigate(`/production/orders/${orderId}`);
    } catch (error) {
      console.error('Error creating return:', error);
      // Toast error is handled by the API client interceptor
    } finally {
      setSubmitting(false);
    }
  };

  // Filter out DISCOUNT items from selectable items
  const selectableItems = order?.Items?.filter((item: OrderItem) => item.PartNumber !== 'DISCOUNT') || [];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading order details...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!order) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Order Not Found</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">The requested order could not be found.</p>
            <button
              onClick={() => navigate('/production/orders-dashboard')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Orders Dashboard
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex-1 bg-gray-100 dark:bg-gray-900 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          {/* Header */}
          <div className="bg-gray-200 dark:bg-gray-800 px-6 py-4 mb-6 rounded-t-lg">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Create Return / Repair</h1>
          </div>

          {/* Order Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm mb-4">
                  <span className="font-semibold text-gray-900 dark:text-white">Sales Order #:</span>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">{order.InvoiceNumber}</span>
                </div>
                <div className="text-sm">
                  <span className="font-semibold text-gray-900 dark:text-white">Customer:</span>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">{order.BillToName}</span>
                </div>
              </div>
              <div>
                <div className="font-semibold text-gray-900 dark:text-white mb-2">Ship To:</div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded text-sm">
                  <div className="text-gray-700 dark:text-gray-300">{order.ShipToName}</div>
                  {order.ShipToAddress1 && <div className="text-gray-700 dark:text-gray-300">{order.ShipToAddress1}</div>}
                  {order.ShipToAddress2 && <div className="text-gray-700 dark:text-gray-300">{order.ShipToAddress2}</div>}
                  <div className="text-gray-700 dark:text-gray-300">
                    {order.ShipToCity}{order.ShipToCity && ', '}{order.ShipToState} {order.ShipToZipCode}
                  </div>
                  {order.ShipToCountry && <div className="text-gray-700 dark:text-gray-300">{order.ShipToCountry}</div>}
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Part Numbers to Return */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
              <label className="block font-semibold text-gray-900 dark:text-white mb-4">
                Part Numbers to Return
              </label>
              <div className="space-y-3">
                {selectableItems.map((item: OrderItem) => (
                  <label key={item.Id} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPartNumbers.includes(item.PartNumber)}
                      onChange={() => handleItemToggle(item.PartNumber)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item.PartNumber}
                      {item.Description && <span className="text-gray-500 dark:text-gray-400 ml-2">- {item.Description}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Order Tagged As */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
              <label className="block font-semibold text-gray-900 dark:text-white mb-4">
                Order tagged as
              </label>
              <div className="flex flex-wrap gap-6">
                {returnTags.map((tag) => (
                  <label key={tag.Id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="orderTag"
                      value={tag.Id}
                      checked={selectedTagId === tag.Id}
                      onChange={() => setSelectedTagId(tag.Id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">{tag.Name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Return Reason */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
              <label className="block font-semibold text-gray-900 dark:text-white mb-4">
                Return Reason
              </label>
              <textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Please describe the reason for this return..."
              />
            </div>

            {/* Return Type */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
              <label className="block font-semibold text-gray-900 dark:text-white mb-4">
                Return Type
              </label>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="returnType"
                    value="Repair"
                    checked={returnType === 'Repair'}
                    onChange={(e) => setReturnType(e.target.value as ReturnType)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Repair</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="returnType"
                    value="Return"
                    checked={returnType === 'Return'}
                    onChange={(e) => setReturnType(e.target.value as ReturnType)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Return</span>
                </label>
              </div>
            </div>

            {/* Customer Email Notification */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
              <label className="block font-semibold text-gray-900 dark:text-white mb-4">
                Customer Email Notification
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Enter the customer's email to send them the return authorization details.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Customer Email
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="customer@email.com"
                  />
                </div>
              </div>
            </div>

            {/* Free Shipping Labels */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
              <label className="block font-semibold text-gray-900 dark:text-white mb-4">
                Free Shipping Labels
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={freeShippingLabels}
                  onChange={(e) => setFreeShippingLabels(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 dark:text-gray-300">Shipping Labels Provided</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate(`/production/orders/${orderId}`)}
                className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Return'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default CreateReturn;
